import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { SparkActivityStatus } from '../../types/spark-activity';

interface ActivityActionBarProps {
  onSave: () => void;
  onStatusTransition: () => void;
  onArchive: () => void;
  onPrev: () => void;
  onNext: () => void;
  isSaving: boolean;
  isArchiving: boolean;
  isDirty: boolean;
  status: SparkActivityStatus;
  currentIndex: number;
  total: number;
  /** Set false to hide the Go live / Unpublish button (e.g. when the transition isn't allowed) */
  canTransition?: boolean;
  /** Set false to hide the Archive button */
  canArchive?: boolean;
  /** Set true to hide the Save button (read-only activities) */
  hideSave?: boolean;
}

export default function ActivityActionBar({
  onSave,
  onStatusTransition,
  onArchive,
  onPrev,
  onNext,
  isSaving,
  isArchiving,
  isDirty,
  status,
  currentIndex,
  total,
  canTransition = true,
  canArchive = true,
  hideSave = false,
}: ActivityActionBarProps) {
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (!isSaving && !isDirty) {
      setShowSaved(true);
      const t = setTimeout(() => setShowSaved(false), 1800);
      return () => clearTimeout(t);
    }
  }, [isSaving, isDirty]);

  const transitionLabel =
    status === 'draft' ? 'Go live'
    : status === 'published' ? 'Unpublish'
    : status === 'unpublished' ? 'Go live'
    : null;

  return (
    <div
      className="sticky bottom-0 flex items-center gap-3 rounded-b-[14px]"
      style={{
        margin: '28px -30px -30px',
        padding: '15px 30px',
        background: '#fff',
        borderTop: '1px solid var(--pine-line)',
      }}
    >
      {!hideSave && (
        <button
          onClick={onSave}
          disabled={isSaving || !isDirty}
          className="admin-button-secondary disabled:opacity-40"
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      )}

      {transitionLabel && canTransition && (
        <button
          data-testid="publish-button"
          onClick={onStatusTransition}
          disabled={isSaving || isArchiving}
          className="admin-button-primary disabled:opacity-40"
        >
          {transitionLabel}
        </button>
      )}

      {status !== 'archived' && canArchive && (
        <button
          data-testid="archive-button"
          onClick={onArchive}
          disabled={isSaving || isArchiving}
          className="px-4 py-2 text-sm font-semibold rounded-pine-input border-[1.5px] border-pine-danger text-pine-danger hover:bg-pine-danger hover:text-white transition-colors disabled:opacity-50"
        >
          Archive
        </button>
      )}

      {showSaved && !hideSave && (
        <span className="text-[12px] text-pine-approved font-medium animate-in fade-in duration-200">
          Saved
        </span>
      )}

      {isDirty && !isSaving && (
        <span className="text-[12px] text-pine-ink-soft">Unsaved changes</span>
      )}

      <div className="flex items-center gap-1 ml-auto">
        <button
          data-testid="prev-button"
          onClick={onPrev}
          disabled={currentIndex === 0}
          className="p-1.5 rounded-md text-pine-ink-soft hover:text-pine-ink hover:bg-pine-hover-item disabled:opacity-30 disabled:cursor-not-allowed transition-colors border-[1.5px] border-pine-line"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-pine-ink-soft px-1">
          {currentIndex + 1} / {total}
        </span>
        <button
          data-testid="next-button"
          onClick={onNext}
          disabled={currentIndex === total - 1}
          className="p-1.5 rounded-md text-pine-ink-soft hover:text-pine-ink hover:bg-pine-hover-item disabled:opacity-30 disabled:cursor-not-allowed transition-colors border-[1.5px] border-pine-line"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
